import { LESSON_STYLE } from '@yandex-int/k-common/typings';
import b from 'b_';
import { Edit as PencilIcon } from 'education-icons';
import { Input, Text } from 'platform-components';
import { INPUT_SIZES, INPUT_THEMES, KEY_CODES } from 'platform-components/constants';
import { InputChangeEvent, InputKeyPressEvent } from 'platform-components/src/typings';
import {
    InjectedProps as WithRouterPathProps,
    withRouterPath,
} from 'platform-components/src/utils/hocs/with-router-path/with-router-path';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { LIBRARY_CONTENT_TYPE } from 'common.components/constants';
import lessonActions from 'common.components/lesson/actions';
import * as lessonSelectors from 'common.components/lesson/selectors';

import './lesson-view__lesson-name.scss';

export const TITLE_MAX_LENGTH = 82;

interface StoreProps {
    lessonTitle: string;
    isPreparedLesson: boolean;
    lessonStyle: LESSON_STYLE;
    shouldShowBanner: boolean;
}

interface DispatchProps {
    saveLesson: typeof lessonActions.saveLesson;
    setLessonName: typeof lessonActions.setName;
}

interface Props extends StoreProps, DispatchProps, WithRouterPathProps {
    canEdit: boolean;
}

export const LessonName = (props: Props) => {
    const {
        canEdit,
        lessonTitle: savedLessonTitle,
        setLessonName,
        saveLesson,
        match,
        isPreparedLesson,
        lessonStyle,
        shouldShowBanner,
    } = props;
    const subject = match?.params?.subject;

    const [isEditing, setIsEditing] = useState(false);
    const [lessonTitle, setTitle] = useState(savedLessonTitle);

    useEffect(() => {
        setTitle(savedLessonTitle);
    }, [savedLessonTitle]);

    const isTitleValid = () => lessonTitle.trim().length > 0 && lessonTitle.trim().length < TITLE_MAX_LENGTH;

    const handleClickIcon = () => {
        setIsEditing(true);
    };

    const saveLessonName = () => {
        if (isTitleValid()) {
            setIsEditing(false);
            setLessonName({ lessonName: lessonTitle.trim() });
            saveLesson();

            ym('reachGoal', 'teacher_lesson_creation_rename', {
                subject,
                contentType: isPreparedLesson
                    ? LIBRARY_CONTENT_TYPE.PREPARED_LESSON
                    : LIBRARY_CONTENT_TYPE.COURSE_LESSON,
            });
        }
    };

    const handleChangeInput = ({ target: { value } }: InputChangeEvent) => {
        setTitle(value);
    };

    const handleTitleEnterPress = ({ keyCode }: InputKeyPressEvent) => {
        if (keyCode === KEY_CODES.ENTER) {
            saveLessonName();
        }
    };

    const setTitleInputRef = (ref: HTMLInputElement) => {
        if (ref) {
            // таймаут нужен, чтобы инпут успел отрендериться и зафокуситься
            setTimeout(() => {
                ref.setSelectionRange(0, lessonTitle.length);
            }, 50);
        }
    };

    if (isEditing) {
        return (
            <div className="lesson-view__lesson-name">
                <Input
                    focusInputEnd
                    focused
                    maxLength={TITLE_MAX_LENGTH}
                    mix="lesson-view__lesson-name-input"
                    onBlur={saveLessonName}
                    onChange={handleChangeInput}
                    onControlRef={setTitleInputRef}
                    onKeyUp={handleTitleEnterPress}
                    size={INPUT_SIZES.L}
                    theme={INPUT_THEMES.YANDEX}
                    valid={isTitleValid() ? 'uncertain' : 'no'}
                    value={lessonTitle}
                />
            </div>
        );
    }

    return (
        <div
            className={b('lesson-view', 'lesson-name', {
                'lesson-style': lessonStyle,
                big: shouldShowBanner,
            })}
        >
            <Text size={Text.SIZE.XXXL} weight={Text.WEIGHT.M}>
                {lessonTitle}
            </Text>
            {canEdit && <PencilIcon onClick={handleClickIcon} />}
        </div>
    );
};

const mapStateToProps = (storeState: any): StoreProps => {
    return {
        lessonTitle: lessonSelectors.getName(storeState) || '',
        isPreparedLesson: lessonSelectors.getIsPreparedLesson(storeState),
        lessonStyle: lessonSelectors.getLessonStyle(storeState),
        shouldShowBanner: lessonSelectors.getShouldShowBanner(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            saveLesson: lessonActions.saveLesson,
            setLessonName: lessonActions.setName,
        },
        dispatch
    );
};

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(LessonName));
